import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

function formatDMY(val) {
  if (!val) return "";

  const s = String(val).trim();
  const onlyDate = s.slice(0, 10);

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (m) {
    return `${m[3]}/${m[2]}/${m[1]}`;
  }

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return s;
}

export default function Injection() {
  const navigate = useNavigate();

  const [filterDate, setFilterDate] = useState("");
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openHeaderId, setOpenHeaderId] = useState(null);
  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const pageGroupSize = 20;

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const role = String(currentUser?.role || "").toLowerCase();

  const isUser = role === "user";
  const isOperator = role === "operator";
  const isAdmin = role === "admin";

  const canView = isUser || isOperator || isAdmin;
  const canPrint = isUser || isOperator || isAdmin;
  const canAdd = isOperator || isAdmin;
  const canEdit = isOperator || isAdmin;
  const canDelete = isAdmin;

  useEffect(() => {
    loadHeaders();
  }, []);

  async function loadHeaders() {
    setLoading(true);
    try {
      const res = await api.get(`/api/injection/headers`);
      setHeaders(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load records"
      );
    } finally {
      setLoading(false);
    }
  }

  const filteredHeaders = useMemo(() => {
    if (!filterDate) return headers;
    return headers.filter(
      (h) => String(h.productionDate || "").slice(0, 10) === filterDate
    );
  }, [headers, filterDate]);

  const totalPages = Math.ceil(filteredHeaders.length / pageSize);

  const paginatedHeaders = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredHeaders.slice(start, start + pageSize);
  }, [filteredHeaders, currentPage]);

  const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
  const startPage = currentGroup * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterDate]);

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  }

  async function viewEntry(headerId) {
    if (openHeaderId === headerId) {
      setOpenHeaderId(null);
      setLines([]);
      return;
    }

    setOpenHeaderId(headerId);
    setLines([]);
    setLinesLoading(true);

    try {
      const res = await api.get(`/api/injection/headers/${headerId}/lines`);
      setLines(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Failed to load details"
      );
      setOpenHeaderId(null);
    } finally {
      setLinesLoading(false);
    }
  }

  function printEntry(headerId) {
    window.open(`/injection/print/${headerId}`, "_blank");
  }

  function editEntry(headerId) {
    navigate(`/injection/edit/${headerId}`);
  }

  async function deleteEntry(headerId) {
    const ok = window.confirm(`Delete Entry ID ${headerId}?`);
    if (!ok) return;

    try {
      await api.delete(`/api/injection/headers/${headerId}`);
      alert("Deleted successfully");
      setOpenHeaderId(null);
      setLines([]);
      await loadHeaders();
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Delete failed"
      );
    }
  }

  return (
    <div className="container-fluid" style={{ padding: 16 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">Injection - View Records</h2>

            {canAdd && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/injection/add")}
              >
                + Add
              </button>
            )}
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-center">
                <div className="col-md-5">
                  <input
                    type="date"
                    className="form-control"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                  />
                </div>

                <div className="col-md-5">
                  <button type="button" className="btn btn-primary w-100">
                    Search
                  </button>
                </div>

                <div className="col-md-2 text-md-end">
                  {loading ? <span className="text-muted">Loading...</span> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 110 }}>Serial No.</th>
                      <th style={{ width: 140 }}>Entry ID</th>
                      <th style={{ width: 180 }}>Date</th>
                      <th style={{ width: 140 }}>Shift</th>
                      <th style={{ width: 290 }} className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedHeaders.map((r, idx) => (
                      <tr key={r.headerId ?? idx}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{r.headerId}</td>
                        <td>{formatDMY(r.productionDate)}</td>
                        <td>{r.shift || "-"}</td>

                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            {canView && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => viewEntry(r.headerId)}
                              >
                                {openHeaderId === r.headerId ? "Hide" : "View"}
                              </button>
                            )}

                            {canPrint && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => printEntry(r.headerId)}
                              >
                                Print
                              </button>
                            )}

                            {canEdit && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => editEntry(r.headerId)}
                              >
                                Edit
                              </button>
                            )}

                            {canDelete && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteEntry(r.headerId)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {filteredHeaders.length === 0 && !loading && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted p-4">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card-footer">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                  <div>
                    <b>Total: {filteredHeaders.length}</b>
                  </div>

                  {totalPages > 0 && (
                    <div className="d-flex gap-1 flex-wrap justify-content-center">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(startPage - 1)}
                        disabled={startPage === 1}
                      >
                        &laquo;
                      </button>

                      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const page = startPage + i;
                        return (
                          <button
                            key={page}
                            className={`btn btn-sm ${
                              currentPage === page
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(endPage + 1)}
                        disabled={endPage >= totalPages}
                      >
                        &raquo;
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {openHeaderId && (
                <div className="p-3 border-top">
                  <h6 className="mb-2">Entry Details (Entry ID: {openHeaderId})</h6>

                  {linesLoading ? (
                    <div className="text-muted">Loading details...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 60 }}>#</th>
                            <th style={{ width: 220 }}>Machine</th>
                            <th style={{ width: 160 }}>Item Code</th>
                            <th style={{ minWidth: 220 }}>Item Name</th>
                            <th style={{ width: 120 }} className="text-end">PCS</th>
                            <th style={{ width: 120 }} className="text-end">Weight</th>
                            <th style={{ width: 120 }} className="text-end">Total</th>
                          </tr>
                        </thead>

                        <tbody>
                          {lines.map((l, i) => {
                            const machine =
                              l.machineName ??
                              l.machineNo ??
                              l.machine_name ??
                              "-";

                            const itemCode = l.itemCode ?? l.item_code ?? "-";

                            const itemName =
                              l.productName ??
                              l.itemName ??
                              l.item_name ??
                              "-";

                            const pcs = l.totalPcs ?? l.total_pcs ?? 0;
                            const weight = l.pcsWeight ?? l.pcs_weight ?? 0;
                            const total = l.totalWeight ?? l.total_weight ?? 0;

                            return (
                              <tr key={l.lineId ?? i}>
                                <td>{i + 1}</td>
                                <td>{machine}</td>
                                <td>{itemCode}</td>
                                <td>{itemName}</td>
                                <td className="text-end">{pcs}</td>
                                <td className="text-end">{weight}</td>
                                <td className="text-end fw-semibold">{total}</td>
                              </tr>
                            );
                          })}

                          {lines.length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center text-muted">
                                No line items found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}